import { Col, Form, InputNumber, Radio, Row, Select } from "antd";
import { InputWithLabelAndSvg } from "components/Global";
import { FORMDATA } from "constants/Data";
import React, { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "redux/store";
import styles from "./index.module.scss";
import { SideDrawerBodyProps } from "./types";
import { editUserAction, inviteUserAction } from "redux/action/userAction";
import { SingleUserInput } from "components/Global/InputWithLabel/types";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { phoneNumberFormatter } from "helper/phoneNumberFormatter";
import { invalidText } from "utils/utils";
import { getApi } from "redux/apis";
import {
  parsePhoneNumber,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
} from "libphonenumber-js";

const AddUserBody: FC<SideDrawerBodyProps> = (props) => {
  // Inits
  const { addUserFields } = FORMDATA;
  const { closeDrawerByAnimation, editSelectedUser, fetchUsers } = props;
  const [form] = Form.useForm();

  // State Management
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [isSelected, setIsSelected] = useState<boolean>();
  const [phoneNumber, setPhoneNumber] = useState(
    editSelectedUser ? editSelectedUser.simplePhone : ""
  );
  const [phoneError, setPhoneError] = useState("");

  const editData = {
    ...(editSelectedUser || {}),
    fullName: editSelectedUser?.name || "",
    phone: editSelectedUser?.simplePhone,
    customerInventoryViewMode:
      editSelectedUser?.customerInventoryViewMode || "COMMERCIAL",
    customerAvailabilityThresholdHigh:
      editSelectedUser?.customerAvailabilityThresholdHigh ?? 20,
    customerAvailabilityThresholdLow:
      editSelectedUser?.customerAvailabilityThresholdLow ?? 5,
  };

  useEffect(() => {
    setValidateDisplayNum(editSelectedUser ? editSelectedUser.simplePhone : "");
    // If editing an existing user with a phone number, mark as interacted
    if (editSelectedUser && editSelectedUser.simplePhone) {
      setHasUserInteracted(true);
    }
  }, [editSelectedUser]);

  const [showError, setShowError] = useState<boolean>();
  const [displayNum, setDisplayNum] = useState(
    editSelectedUser ? editSelectedUser.simplePhone : ""
  );

  const CUSTOMER_THRESHOLD_MAX = 1000000000 as number; // 1e9 (safe domain + prevents bigint overflows)
  const CUSTOMER_THRESHOLD_MAX_DIGITS: number = String(
    CUSTOMER_THRESHOLD_MAX
  ).length;
  const onlyDigits = (val: string) => val.replace(/\D/g, "");
  const lastValidHighRef = useRef<number>(
    editData.customerAvailabilityThresholdHigh ?? 20
  );
  const lastValidLowRef = useRef<number>(
    editData.customerAvailabilityThresholdLow ?? 5
  );

  // Strict digits-only + immediate max clamping.
  // This prevents alphabets from ever appearing (not even temporarily),
  // and ensures values > max get clamped right away on change.
  const clampThreshold = (raw: number | null | undefined, min: number) => {
    if (raw == null) return raw;
    const intVal = Math.floor(raw);
    if (!Number.isFinite(intVal)) return raw;
    // NOTE: do not clamp-to-max here; we block/reject values over max elsewhere
    // to avoid the field "jumping" to max while typing.
    return Math.max(intVal, min);
  };

  const handleThresholdKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const allowedNavigationKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Enter",
      "Home",
      "End",
    ];

    if (e.ctrlKey || e.metaKey) return; // allow copy/paste/select all shortcuts
    if (allowedNavigationKeys.includes(e.key)) return;
    if (/^\d$/.test(e.key)) {
      // Prevent typing beyond max digits / max numeric value.
      const input = e.currentTarget;
      const selectionStart = input.selectionStart ?? input.value.length;
      const selectionEnd = input.selectionEnd ?? input.value.length;
      const nextRaw =
        (input.value || "").slice(0, selectionStart) +
        e.key +
        (input.value || "").slice(selectionEnd);
      const nextDigits = onlyDigits(nextRaw);
      const nextNum = nextDigits === "" ? 0 : Number(nextDigits);
      if (
        nextDigits.length > CUSTOMER_THRESHOLD_MAX_DIGITS ||
        (Number.isFinite(nextNum) && nextNum > CUSTOMER_THRESHOLD_MAX)
      ) {
        e.preventDefault();
      }
      return;
    }

    // Block letters and all non-digit keys (including '.', '-', etc.)
    e.preventDefault();
  };

  const handleThresholdPaste = (
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = e.clipboardData.getData("text") ?? "";
    const normalized = text.replace(/\s/g, "");
    if (normalized === "") return;

    if (!/^\d+$/.test(normalized)) {
      e.preventDefault();
      return;
    }

    // Stop pasting very large digit sequences / values over max.
    if (
      normalized.length > CUSTOMER_THRESHOLD_MAX_DIGITS ||
      Number(normalized) > CUSTOMER_THRESHOLD_MAX
    ) {
      e.preventDefault();
      return;
    }
  };
  const [validateDisplayNum, setValidateDisplayNum] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track user interaction

  const dispatch = useDispatch<AppDispatch>();
  const roleData = useSelector((state: any) => state?.roleTable?.data);
  const customerRoleId = (roleData || []).find(
    (r: any) => r.roleName === "Customer"
  )?.id;
  const effectiveRoleId = selectedRole || editSelectedUser?.roleId;
  const isCustomerRole =
    !!customerRoleId &&
    effectiveRoleId === customerRoleId;
  const customerInventoryViewMode = Form.useWatch(
    "customerInventoryViewMode",
    form
  );
  const { isAddUserLoading } = useSelector((state: any) => state?.users);
  const profileData = useSelector((state: any) => state?.userProfile?.data);
  const loggedInRoleName = profileData?.companyRoles[0]?.role?.roleName;
  const loggedInroleId = profileData?.companyRoles[0]?.roleId;

  const [saveAndAddLoader, setSaveAndAddLoader] = useState(false);

  const [isEditLoading, setIsEditLoading] = useState(false);
  const [serviceCenterOptions, setServiceCenterOptions] = useState([]);
  const [selectedServiceCenters, setSelectedServiceCenters] = useState<
    string[]
  >([]);
  const [disableServiceCenters, setDisableServiceCenters] =
    useState<boolean>(false);
  const [loadingServiceCenters, setLoadingServiceCenters] = useState(false);
  const [isServiceCenterRole, setIsServiceCenterRole] = useState(false);

  useEffect(() => {
    const data: any = roleData
      .filter((role: any) => role.roleName !== "Admin 3nStar")
      .map((role: any) => ({
        value: role.id,
        label: role.roleName,
      }));

    setRoleOptions(data);
  }, []);

  // Track previous country to detect country changes
  const [previousCountry, setPreviousCountry] = useState<string>("us");

  // Enhanced phone number validation using libphonenumber-js
  const validatePhoneNumber = (phoneNumber: string, country: any) => {
    if (!phoneNumber || phoneNumber.trim() === "") {
      return { isValid: false, error: "Phone number is required" };
    }

    try {
      const countryCode = (
        country?.countryCode ||
        country?.iso2 ||
        ""
      ).toUpperCase();

      // Use libphonenumber-js for dynamic validation - no need for manual country rules

      // Parse the phone number with country context
      const parsedNumber = parsePhoneNumber(phoneNumber, countryCode as any);

      if (!parsedNumber) {
        return { isValid: false, error: "Invalid phone number" };
      }

      // Check if the number is valid for the specific country
      if (!parsedNumber.isValid()) {
        return {
          isValid: false,
          error: "Invalid phone number",
        };
      }

      // Additional validation: Check total digit count including country code
      const dialCode = country?.dialCode || "";
      const digitsOnly = phoneNumber.replace(/\D/g, "");

      // Calculate expected total digits (country code + national number)
      const expectedTotalDigits =
        dialCode.length + parsedNumber.nationalNumber.length;

      // Validate total digit count
      if (digitsOnly.length !== expectedTotalDigits) {
        return {
          isValid: false,
          error: "Invalid phone number",
        };
      }

      // Ensure the number starts with the correct country code
      if (!digitsOnly.startsWith(dialCode)) {
        return {
          isValid: false,
          error: "Invalid phone number",
        };
      }

      return { isValid: true, error: "" };
    } catch (error) {
      // Fallback validation if libphonenumber-js fails
      try {
        // Try global validation without country
        if (isValidPhoneNumber(phoneNumber)) {
          return { isValid: true, error: "" };
        } else {
          return { isValid: false, error: "Invalid phone number" };
        }
      } catch (fallbackError) {
        return { isValid: false, error: "Invalid phone number" };
      }
    }
  };

  const phoneChangeHandler = (value: string, country: any) => {
    // Mark that user has interacted with the phone input
    setHasUserInteracted(true);

    const formattedValue = phoneNumberFormatter(value, country?.dialCode);
    setDisplayNum(formattedValue?.formattedPhoneNumber);
    setValidateDisplayNum(formattedValue?.cleanedPhoneNumber);

    // Check if country changed
    const currentCountryCode = country?.countryCode || country?.iso2;
    const isCountryChange =
      previousCountry && previousCountry !== currentCountryCode;

    // Enhanced validation using libphonenumber-js - only show errors after user interaction
    const validation = validatePhoneNumber(value, country);
    if (!validation.isValid && hasUserInteracted) {
      setPhoneError(validation.error);
      setShowError(true);
    } else if (validation.isValid) {
      setPhoneError("");
      setShowError(false);
    }
    // Don't show error initially if user hasn't interacted yet

    // Focus management: Focus the input after phone number change or country change
    const focusDelay = isCountryChange ? 200 : 50; // Longer delay for country changes
    setTimeout(() => {
      const phoneInputElement = document.querySelector(
        ".react-tel-input input"
      ) as HTMLInputElement;
      if (phoneInputElement) {
        phoneInputElement.focus();
        // Position cursor at the end of the input
        const length = phoneInputElement.value.length;
        phoneInputElement.setSelectionRange(length, length);
      }
    }, focusDelay);

    // Update previous country
    setPreviousCountry(currentCountryCode);
  };

  const checkEmailValidation = async () => {
    try {
      await form.validateFields(["email"]);
      return true;
    } catch (error) {
      return false;
    }
  };

  // If form get success
  const onFinish = (values: any, buttonType = "Save") => {
    // First validate the form completely before proceeding
    const isFormValid = validateForm();
    if (!isFormValid) {
      return; // Stop execution if validation fails
    }

    const fullName = (values.fullName as string)?.trim();
    const nameArray = fullName.split(" ");
    const firstName = nameArray.slice(0, 1).join(" ") || "";
    const lastName = nameArray.slice(1, nameArray.length).join(" ") || "";

    if (editSelectedUser) {
      setIsEditLoading(true);
      setSaveAndAddLoader(true);
      let data: any = {
        userId: editSelectedUser?.userId,
        phone: displayNum,
        firstName,
        lastName,
        serviceCenters: selectedServiceCenters || [],
      };

      if (selectedRole) {
        data = {
          ...data,
          roleId: selectedRole,
          isChangeStatus: false,
        };
      }

      if (isCustomerRole) {
        data = {
          ...data,
          customerInventoryViewMode: values.customerInventoryViewMode,
          customerAvailabilityThresholdHigh:
            values.customerAvailabilityThresholdHigh,
          customerAvailabilityThresholdLow:
            values.customerAvailabilityThresholdLow,
        };
      }

      dispatch(editUserAction(data)).then(() => {
        closeDrawerByAnimation();
        fetchUsers();
      });
    } else {
      setIsSelected(true);
      buttonType === "SaveAndAdd" && setSaveAndAddLoader(true);
      const finalData: Record<string, unknown> = {
        email: values.email.toLowerCase(),
        role: selectedRole,
        phone: displayNum,
        firstName,
        lastName,
        serviceCenters: selectedServiceCenters || [],
      };

      if (selectedRole === customerRoleId) {
        finalData.customerInventoryViewMode =
          values.customerInventoryViewMode;
        finalData.customerAvailabilityThresholdHigh =
          values.customerAvailabilityThresholdHigh;
        finalData.customerAvailabilityThresholdLow =
          values.customerAvailabilityThresholdLow;
      }
      dispatch(inviteUserAction(finalData)).then((res) => {
        if (res?.payload?.data) {
          setSelectedServiceCenters([]);
          form.resetFields();
          setPhoneNumber(editSelectedUser ? editSelectedUser.simplePhone : "");
          setDisplayNum("");
          setSelectedRole("");
          setHasUserInteracted(false); // Reset interaction state for new user
          fetchUsers();
          buttonType === "Save" && closeDrawerByAnimation();
        }
        buttonType === "SaveAndAdd" && setSaveAndAddLoader(false);
      });
    }
  };

  // handle Save and Add
  const saveAndAdd = async () => {
    const formValues = form.getFieldsValue();

    try {
      // Validate all form fields including email
      await form.validateFields();
      const isEmailValidate = await checkEmailValidation();

      // Validate the complete form including phone number
      const isFormValid = validateForm();

      if (isEmailValidate && isFormValid && formValues?.fullName) {
        onFinish(formValues, "SaveAndAdd");
      }
    } catch (error) {
      // Form validation failed, errors will be shown by validateForm()
      console.log("Form validation failed:", error);
    }
  };

  // Enhanced form validation function
  const validateForm = () => {
    let isValid = true;

    // Mark that user has interacted when form is submitted
    setHasUserInteracted(true);

    // Validate phone number
    if (invalidText(validateDisplayNum)) {
      setPhoneError("Phone number is required");
      setShowError(true);
      isValid = false;
    } else {
      // Get current country info for validation
      const phoneInputElement = document.querySelector(
        ".react-tel-input .selected-flag"
      ) as HTMLElement;
      const countryCode =
        phoneInputElement
          ?.getAttribute("title")
          ?.split(":")[0]
          ?.toLowerCase() || "us";

      // Create a mock country object for validation
      const mockCountry = {
        countryCode: countryCode,
        iso2: countryCode,
        dialCode: displayNum.match(/^\+(\d+)/)?.[1] || "1",
      };

      const validation = validatePhoneNumber(displayNum, mockCountry);
      if (!validation.isValid) {
        setPhoneError(validation.error);
        setShowError(true);
        isValid = false;
      } else {
        setPhoneError("");
        setShowError(false);
      }
    }

    // Validate role selection
    if (!selectedRole) {
      setIsSelected(false);
      isValid = false;
    } else {
      setIsSelected(true);
    }

    // Validate service centers if required
    const formValues = form.getFieldsValue();
    if (
      isServiceCenterRole &&
      (!formValues.serviceCenters || formValues.serviceCenters.length === 0)
    ) {
      form.setFields([
        {
          name: "serviceCenters",
          errors: ["Please select at least one service center"],
        },
      ]);
      isValid = false;
    }

    // Log validation result for debugging
    if (!isValid) {
      console.log("Form validation failed:", {
        phoneError: phoneError,
        hasPhoneNumber: !invalidText(validateDisplayNum),
        hasRole: !!selectedRole,
        hasServiceCenters: isServiceCenterRole
          ? formValues.serviceCenters?.length > 0
          : true,
      });
    }

    return isValid;
  };

  // If form fails
  const onFinishFailed = () => {
    validateForm();
  };

  const fetchServiceCenters = async (
    loggedInRoleId: string,
    roleId?: string
  ) => {
    setLoadingServiceCenters(true);
    console.log("loggedInRoleId: ", loggedInRoleId);
    try {
      const response = await getApi(
        `/serviceCenter/dropdown?role=${loggedInRoleId}`
      );
      console.log("response: ", response);
      const centers = response.data.data.map((center: any) => ({
        value: center.id,
        label: center.name,
      }));
      setServiceCenterOptions(centers);

      const employeeRoleId = roleData.find(
        (role: any) => role.roleName === "Service Center"
      )?.id;

      // If roleId is provided, use it. Otherwise, use editSelectedUser's roleId
      const currentRoleId = roleId || editSelectedUser?.roleId;
      console.log("currentRoleId: ", currentRoleId);

      // Determine if the current role is Employee
      const isServiceCenterRole = currentRoleId === employeeRoleId;
      console.log("isServiceCenterRole: ", isServiceCenterRole);
      setIsServiceCenterRole(isServiceCenterRole);
      let selectedIds: string[] = [];

      if (editSelectedUser) {
        // If editing, pre-select service centers from editSelectedUser
        selectedIds = editSelectedUser.serviceCenters.map(
          (center: any) => center.serviceCenterId
        );
      }

      setDisableServiceCenters(false);

      setSelectedServiceCenters(selectedIds);
      form.setFieldsValue({ serviceCenters: selectedIds });
    } catch (error) {
      console.error("Error fetching service centers:", error);
      setServiceCenterOptions([]);
    } finally {
      setLoadingServiceCenters(false);
    }
  };

  const handleRoleChange = (roleId: string) => {
    setSelectedRole(roleId);
    setIsSelected(true);
    setSelectedServiceCenters([]);
    setIsServiceCenterRole(false);
    if (roleId === customerRoleId) {
      form.setFieldsValue({
        customerInventoryViewMode: "COMMERCIAL",
        customerAvailabilityThresholdHigh: 20,
        customerAvailabilityThresholdLow: 5,
      });
      lastValidHighRef.current = 20;
      lastValidLowRef.current = 5;
    } else {
      form.setFieldsValue({
        customerInventoryViewMode: undefined,
      });
    }
    form.setFields([
      {
        name: "serviceCenters", // Ensure this matches your form's field name
        errors: [],
      },
    ]);
    fetchServiceCenters(loggedInroleId, roleId);
  };

  const handleSelectChange = (selectedValues: string[]) => {
    setSelectedServiceCenters(selectedValues);
    form.setFieldsValue({ serviceCenters: selectedValues });

    if (selectedValues.length > 0) {
      form.setFields([
        {
          name: "serviceCenters",
          errors: [],
        },
      ]);
    }
  };

  useEffect(() => {
    if (editSelectedUser) {
      setSelectedRole(editSelectedUser.roleId);
      if (editSelectedUser.roleId === customerRoleId) {
        form.setFieldsValue({
          customerInventoryViewMode:
            editSelectedUser.customerInventoryViewMode || "COMMERCIAL",
          customerAvailabilityThresholdHigh:
            editSelectedUser.customerAvailabilityThresholdHigh ?? 20,
          customerAvailabilityThresholdLow:
            editSelectedUser.customerAvailabilityThresholdLow ?? 5,
        });
        lastValidHighRef.current =
          editSelectedUser.customerAvailabilityThresholdHigh ?? 20;
        lastValidLowRef.current =
          editSelectedUser.customerAvailabilityThresholdLow ?? 5;
      }
      fetchServiceCenters(loggedInroleId, editSelectedUser.roleId);
    }
  }, [editSelectedUser, customerRoleId]);

  // JSX
  return (
    <div className={styles["side-drawer-body"]}>
      <Form
        form={form}
        name="basic"
        initialValues={editData}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        layout="vertical"
        labelAlign="left"
        className={styles["side-drawer-form"]}
      >
        <div className={styles["side-drawer-form__inputs"]}>
          {addUserFields.map(
            (
              singleField: SingleUserInput,
              index: React.Key | null | undefined
            ) => {
              return (
                <React.Fragment key={index}>
                  {singleField.id === "phone" && (
                    <>
                      {" "}
                      <Form.Item
                        className="side-drawer-form__role"
                        name={singleField.name}
                      >
                        <label
                          className={styles["side-drawer-form__role--label"]}
                        >
                          {singleField.title}{" "}
                          {singleField?.required && (
                            <span
                              className="required-color"
                              style={{ marginLeft: "5px" }}
                            >
                              *
                            </span>
                          )}
                        </label>
                        <PhoneInput
                          country={"us"}
                          value={displayNum}
                          onChange={phoneChangeHandler}
                          isValid={(value, country) => {
                            // Always return true to prevent react-phone-input-2 from applying its own error styles
                            // We handle error display manually
                            return true;
                          }}
                          enableSearch={true}
                          disableSearchIcon={false}
                          countryCodeEditable={false}
                          containerClass={
                            hasUserInteracted && phoneError ? "has-error" : ""
                          }
                          inputClass={`${styles["input-icon__form--input"]} ${
                            styles.customPhoneInput
                          } ${
                            hasUserInteracted && phoneError ? styles.error : ""
                          }`}
                        />
                        {hasUserInteracted &&
                          (phoneError ||
                            (validateDisplayNum === "" && showError)) && (
                            <p
                              className="ant-form-item-explain-error"
                              style={{
                                paddingTop: "8px",
                                paddingBottom: "8px",
                              }}
                            >
                              {phoneError || "Please enter phone number"}
                            </p>
                          )}
                      </Form.Item>
                    </>
                  )}
                  {singleField.id !== "roleName" &&
                    singleField.id !== "phone" &&
                    singleField.id !== "serviceCenters" && (
                      <InputWithLabelAndSvg
                        singleUserInput={singleField}
                        disabled={
                          singleField?.name == "email" &&
                          editSelectedUser &&
                          true
                        }
                        setPhoneNumber={setPhoneNumber}
                        phoneNumber={phoneNumber}
                        phoneError={phoneError}
                        setPhoneError={setPhoneError}
                      />
                    )}
                  {singleField.id === "roleName" && (
                    <Form.Item
                      className="side-drawer-form__role"
                      name={singleField.name}
                    >
                      <label
                        className={styles["side-drawer-form__role--label"]}
                      >
                        {singleField.title}{" "}
                        {singleField?.required && (
                          <span
                            className="required-color"
                            style={{ marginLeft: "5px" }}
                          >
                            *
                          </span>
                        )}
                      </label>
                      <Select
                        placeholder="Select Role"
                        className={styles["side-drawer-form__role--select"]}
                        size="large"
                        onSelect={(role) => {
                          handleRoleChange(role);
                        }}
                        defaultValue={
                          editSelectedUser && editSelectedUser?.roleId
                        }
                      >
                        {roleOptions?.map((role: any, key) => {
                          // If the roleName is "Service Center Employee", show only that option
                          if (loggedInRoleName === "Service Center") {
                            if (role?.label === "Service Center") {
                              return (
                                <Select.Option value={role?.value} key={key}>
                                  {role?.label}
                                </Select.Option>
                              );
                            }
                          } else {
                            // If roleName is not "Service Center Employee", show all options
                            return (
                              <Select.Option value={role?.value} key={key}>
                                {role?.label}
                              </Select.Option>
                            );
                          }

                          return null; // In case no condition is met, return null
                        })}
                      </Select>
                      {isSelected == false && (
                        <p className="ant-form-item-explain-error">
                          Please select role
                        </p>
                      )}
                    </Form.Item>
                  )}
                  {singleField.id === "serviceCenters" && (
                    <Form.Item
                      className="side-drawer-form__role"
                      name={singleField.name}
                      rules={[
                        {
                          required: isServiceCenterRole,
                          message: !selectedRole
                            ? "Please select a role first"
                            : serviceCenterOptions.length === 0
                            ? "No Service Center found, please create at least one service center"
                            : "Please select at least one service center",
                        },
                      ]}
                    >
                      <label
                        className={styles["side-drawer-form__role--label"]}
                      >
                        {singleField.title}
                        {isServiceCenterRole && (
                          <span
                            className="required-color"
                            style={{ marginLeft: "5px" }}
                          >
                            *
                          </span>
                        )}
                      </label>

                      <Select
                        placeholder="Select Service Centers"
                        mode="multiple"
                        className={styles["side-drawer-form__role--select"]}
                        options={serviceCenterOptions.map((option: any) => ({
                          ...option,
                          disabled: disableServiceCenters, // Disable all options if condition is met
                        }))}
                        //  showSearch
                        showSearch={
                          selectedServiceCenters.length <
                          serviceCenterOptions.length
                        }
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          option?.label
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        loading={loadingServiceCenters}
                        disabled={
                          !selectedRole
                          //  || serviceCenterOptions.length === 0
                        }
                        value={selectedServiceCenters}
                        onChange={handleSelectChange}
                      />

                      {/* {isSelected == false && (
                        <p className="ant-form-item-explain-error">
                          Please select service center
                        </p>
                      )} */}
                    </Form.Item>
                  )}
                </React.Fragment>
              );
            }
          )}
        </div>
        {isCustomerRole && (
          <div
            className={styles["side-drawer-form__inputs"]}
            style={{ marginTop: 20 }}
          >
            <div>
              <Form.Item
                className="side-drawer-form__role"
                style={{ marginBottom: 8 }}
              >
                <label className={styles["side-drawer-form__role--label"]}>
                  Inventory View{" "}
                  <span
                    className="required-color"
                    style={{ marginLeft: "5px" }}
                  >
                    *
                  </span>
                </label>
                <Form.Item
                  name="customerInventoryViewMode"
                  noStyle
                  rules={[{ required: true, message: "Select inventory view" }]}
                >
                  <Radio.Group style={{ width: "100%", marginTop: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <Radio value="DETAILED">Detailed (show quantities)</Radio>
                      <Radio value="COMMERCIAL">
                        Commercial (availability bands)
                      </Radio>
                    </div>
                  </Radio.Group>
                </Form.Item>
              </Form.Item>
            </div>
            <div>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.customerInventoryViewMode !==
                  cur.customerInventoryViewMode
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("customerInventoryViewMode") ===
                  "COMMERCIAL" ? (
                    <div
                      style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        padding: "16px",
                        marginTop: 48,
                        marginBottom: 16,
                        background: "#fafafa",
                      }}
                    >
                      <p
                        style={{
                          fontWeight: 600,
                          marginBottom: 12,
                          fontSize: "1.4rem",
                          color: "#27272e",
                        }}
                      >
                        Availability Thresholds
                      </p>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Available above"
                            name="customerAvailabilityThresholdHigh"
                            rules={[
                              { required: true, message: "Required" },
                              {
                                type: "number",
                                min: 1,
                                message: "Must be at least 1",
                              },
                              {
                                type: "number",
                                max: CUSTOMER_THRESHOLD_MAX,
                                message: `Must be <= ${CUSTOMER_THRESHOLD_MAX}`,
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={1}
                              precision={0}
                              controls={false}
                              inputMode="numeric"
                              formatter={(value) =>
                                value == null ? "" : String(value)
                              }
                              parser={(displayValue) => {
                                const digits = onlyDigits(
                                  String(displayValue ?? "")
                                ).slice(0, CUSTOMER_THRESHOLD_MAX_DIGITS);
                                if (digits === "") return null as any;
                                return Number(digits) as any;
                              }}
                              onKeyDown={handleThresholdKeyDown}
                              onPaste={handleThresholdPaste}
                              onChange={(val) => {
                                const next = clampThreshold(
                                  val as number | null | undefined,
                                  1
                                );
                                if (next == null) {
                                  form.setFieldsValue({
                                    customerAvailabilityThresholdHigh: next,
                                  });
                                  return;
                                }
                                if (next > CUSTOMER_THRESHOLD_MAX) {
                                  form.setFieldsValue({
                                    customerAvailabilityThresholdHigh:
                                      lastValidHighRef.current,
                                  });
                                  return;
                                }
                                lastValidHighRef.current = next;
                                form.setFieldsValue({
                                  customerAvailabilityThresholdHigh: next,
                                });
                              }}
                              onBlur={() => {
                                const cur = form.getFieldValue(
                                  "customerAvailabilityThresholdHigh"
                                );
                                const next = clampThreshold(
                                  cur as number | null | undefined,
                                  1
                                );
                                if (next == null) return;
                                if (next > CUSTOMER_THRESHOLD_MAX) {
                                  form.setFieldsValue({
                                    customerAvailabilityThresholdHigh:
                                      lastValidHighRef.current,
                                  });
                                  return;
                                }
                                lastValidHighRef.current = next;
                                form.setFieldsValue({
                                  customerAvailabilityThresholdHigh: next,
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Limited from"
                            name="customerAvailabilityThresholdLow"
                            rules={[
                              { required: true, message: "Required" },
                              {
                                type: "number",
                                min: 0,
                                message: "Must be 0 or greater",
                              },
                              {
                                type: "number",
                                max: CUSTOMER_THRESHOLD_MAX,
                                message: `Must be <= ${CUSTOMER_THRESHOLD_MAX}`,
                              },
                              ({ getFieldValue: gf }) => ({
                                validator(_, value) {
                                  const high = gf(
                                    "customerAvailabilityThresholdHigh"
                                  );
                                  if (
                                    value != null &&
                                    high != null &&
                                    Number(high) <= Number(value)
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        "Must be less than 'Available above'"
                                      )
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              precision={0}
                              controls={false}
                              inputMode="numeric"
                              formatter={(value) =>
                                value == null ? "" : String(value)
                              }
                              parser={(displayValue) => {
                                const digits = onlyDigits(
                                  String(displayValue ?? "")
                                ).slice(0, CUSTOMER_THRESHOLD_MAX_DIGITS);
                                if (digits === "") return null as any;
                                return Number(digits) as any;
                              }}
                              onKeyDown={handleThresholdKeyDown}
                              onPaste={handleThresholdPaste}
                              onChange={(val) => {
                                const next = clampThreshold(
                                  val as number | null | undefined,
                                  0
                                );
                                if (next == null) {
                                  form.setFieldsValue({
                                    customerAvailabilityThresholdLow: next,
                                  });
                                  return;
                                }
                                if (next > CUSTOMER_THRESHOLD_MAX) {
                                  form.setFieldsValue({
                                    customerAvailabilityThresholdLow:
                                      lastValidLowRef.current,
                                  });
                                  return;
                                }
                                lastValidLowRef.current = next;
                                form.setFieldsValue({
                                  customerAvailabilityThresholdLow: next,
                                });
                              }}
                              onBlur={() => {
                                const cur = form.getFieldValue(
                                  "customerAvailabilityThresholdLow"
                                );
                                const next = clampThreshold(
                                  cur as number | null | undefined,
                                  0
                                );
                                if (next == null) return;
                                if (next > CUSTOMER_THRESHOLD_MAX) {
                                  form.setFieldsValue({
                                    customerAvailabilityThresholdLow:
                                      lastValidLowRef.current,
                                  });
                                  return;
                                }
                                lastValidLowRef.current = next;
                                form.setFieldsValue({
                                  customerAvailabilityThresholdLow: next,
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  ) : null
                }
              </Form.Item>
            </div>
          </div>
        )}
        <div className={styles["side-drawer-form__buttons"]}>
          {!editSelectedUser && !isEditLoading && (
            <Form.Item>
              <button
                type="button"
                className={`${
                  styles["side-drawer-form__buttons--save-and-add"]
                } ${isAddUserLoading && "pointer-event-none"}`}
                onClick={saveAndAdd}
              >
                {saveAndAddLoader ? (
                  <img
                    src={`${process.env.REACT_APP_IMAGES_BASE_URL}/assets/gifs/loading-black.gif`}
                    crossOrigin={
                      process.env.REACT_APP_ENV === "local"
                        ? undefined
                        : "anonymous"
                    }
                    height={40}
                  />
                ) : (
                  "Save And New"
                )}
              </button>
            </Form.Item>
          )}
          <Form.Item>
            <button
              type="submit"
              className={`${
                styles[
                  editSelectedUser
                    ? "side-drawer-form__buttons--edit"
                    : "side-drawer-form__buttons--save"
                ]
              } ${isAddUserLoading && "pointer-event-none"}`}
            >
              {isAddUserLoading && (!saveAndAddLoader || isEditLoading) ? (
                <img
                  src={`${process.env.REACT_APP_IMAGES_BASE_URL}/assets/gifs/loading-black.gif`}
                  crossOrigin={
                    process.env.REACT_APP_ENV === "local"
                      ? undefined
                      : "anonymous"
                  }
                  height={40}
                />
              ) : (
                "Save"
              )}
            </button>
          </Form.Item>
          <Form.Item>
            <button
              type="reset"
              className={`${styles["side-drawer-form__buttons--cancel"]}`}
              onClick={closeDrawerByAnimation}
              disabled={isAddUserLoading}
            >
              Cancel
            </button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default AddUserBody;
